import {
  BaseQueryFn,
  createApi,
  fetchBaseQuery,
} from "@reduxjs/toolkit/query/react"
import { EndpointBuilder } from "@reduxjs/toolkit/dist/query/endpointDefinitions"
import {
  RootState,
  dynamicMiddlewaresInstance,
  registerReducer,
} from "@core/store"
import { baseQueryWithReauth } from "@/modules/app/store"
import { SCHEDULES_API_PATH, SHIFT_TYPES_LIMIT } from "../constants"
import { StaffSchedule } from "../interfaces"
import { ScheduleListArgs } from "../model/period"
import { fetchScheduleMap } from "./fetchScheduleMap"

const tagTypes = ["ShiftTypes", "Staff", "Schedules"] as const

const reducerPath = "staffScheduleApi"

// Как в остальных модулях adv (advApi.ts:41-47): нормализуем базу с завершающим слэшем.
const baseAdvApiURL = new URL(
  import.meta.env.REACT_APP_ADV_API,
  window.location.origin,
).toString()
export const baseUrl = baseAdvApiURL.endsWith("/")
  ? baseAdvApiURL
  : `${baseAdvApiURL}/`

const baseQuery: BaseQueryFn = fetchBaseQuery({
  baseUrl,
  prepareHeaders: (headers, { getState }) => {
    const token = (getState() as RootState).auth.token
    if (token) {
      headers.set("authorization", `Bearer ${token}`)
    }
    return headers
  },
})

type ApiEndpointBuilder = EndpointBuilder<
  typeof baseQuery,
  (typeof tagTypes)[number],
  typeof reducerPath
>

const endpoints = (build: ApiEndpointBuilder) => ({
  /* ----------------------- Справочник типов смен ----------------------- */
  shiftTypes: build.query<StaffSchedule.ShiftType[], void>({
    query: () => ({
      url: `${SCHEDULES_API_PATH}shifts/type/list`,
      method: "GET",
      params: { limit: SHIFT_TYPES_LIMIT },
    }),
    providesTags: ["ShiftTypes"],
  }),
  createShiftType: build.mutation<
    StaffSchedule.MessageRes,
    StaffSchedule.CreateShiftTypeBody
  >({
    query: (body) => ({
      url: `${SCHEDULES_API_PATH}shift/type`,
      method: "POST",
      body,
    }),
    invalidatesTags: ["ShiftTypes"],
  }),
  updateShiftType: build.mutation<
    StaffSchedule.MessageRes,
    { id: number; body: StaffSchedule.UpdateShiftTypeBody }
  >({
    query: ({ id, body }) => ({
      url: `${SCHEDULES_API_PATH}shift/type/${id}`,
      method: "PATCH",
      body,
    }),
    invalidatesTags: ["ShiftTypes"],
  }),
  deleteShiftType: build.mutation<StaffSchedule.MessageRes, number>({
    query: (id) => ({
      url: `${SCHEDULES_API_PATH}shift/type/${id}`,
      method: "DELETE",
    }),
    invalidatesTags: ["ShiftTypes"],
  }),

  /* ----------------------------- Сотрудники ---------------------------- */
  staffList: build.query<StaffSchedule.Staff[], void>({
    query: () => ({ url: `${SCHEDULES_API_PATH}staff/list`, method: "GET" }),
    providesTags: ["Staff"],
  }),

  /* ----------------------------- Расписание ---------------------------- */
  schedulesList: build.query<StaffSchedule.ScheduleMap, ScheduleListArgs>({
    // Нормализация ВНУТРИ queryFn: в RTK 1.9.7 transformResponse для
    // queryFn-эндпоинтов не вызывается (спека, раздел 6).
    queryFn: (args, _api, _extra, baseQuery) =>
      fetchScheduleMap(baseQuery, args),
    providesTags: ["Schedules"],
  }),
  createSchedules: build.mutation<
    StaffSchedule.CreateSchedulesRes,
    StaffSchedule.CreateSchedulesBody
  >({
    query: (body) => ({
      url: `${SCHEDULES_API_PATH}create`,
      method: "POST",
      body,
    }),
    // invalidatesTags нет ОСОЗНАННО: оптимизмом владеет usePaintCommit (спека, раздел 6).
    // Инвалидация после каждого мазка перезапрашивала бы весь период на каждое движение кисти.
  }),
  deleteSchedules: build.mutation<
    StaffSchedule.MessageRes,
    StaffSchedule.DeleteSchedulesBody
  >({
    // Батч-удаление одним запросом: DELETE /delete с телом { ids }.
    // Один мазок ластиком = один запрос — частичного отказа не бывает.
    //
    // ВНИМАНИЕ (проверено живым запросом 2026-08-06): этого маршрута на бэкенде ПОКА НЕТ —
    // `DELETE {ADV_API}/schedules/delete` отвечает 404, есть только одиночный
    // `DELETE /schedules/delete/:schedule_id`. До выката батч-эндпоинта ластик не работает
    // (осознанное решение: обход через N одиночных DELETE отвергнут — спека, раздел 6).
    // Если бэкенд не сможет провести тело сквозь nginx в DELETE — здесь меняется
    // единственная строка: method: "POST".
    query: (body) => ({
      url: `${SCHEDULES_API_PATH}delete`,
      method: "DELETE",
      body,
    }),
  }),
})

export const staffScheduleApi = createApi({
  reducerPath,
  tagTypes,
  baseQuery: baseQueryWithReauth(baseQuery),
  endpoints: (build) => ({
    ...endpoints(build),
  }),
})
dynamicMiddlewaresInstance.addMiddleware(staffScheduleApi.middleware)
registerReducer(staffScheduleApi.reducerPath, staffScheduleApi.reducer)

export const {
  useShiftTypesQuery,
  useCreateShiftTypeMutation,
  useUpdateShiftTypeMutation,
  useDeleteShiftTypeMutation,
  useStaffListQuery,
  useSchedulesListQuery,
  useCreateSchedulesMutation,
  useDeleteSchedulesMutation,
} = staffScheduleApi
